import React from "react";
import Select from "react-select";
import { Classes } from "jss";
import {
  withStyles,
  createStyles,
  Theme,
  Paper,
  Typography,
  MenuItem
} from "@material-ui/core";
import { TextField } from "./TextField";
import classNames from "../../utils/classNames";

export interface Suggestion {
  label: string;
  value: any;
}

type Props = {
  classes: Classes;
  className?: string;
  id: string;
  label: string;
  isDisabled?: boolean;
  placeholder: string;
  suggestions: Suggestion[];
  onChange: (e: any) => void;
  theme: Theme;
  value?: string | null;
};

type State = {};

const styles = (theme: Theme) =>
  createStyles({
    root: {
      minWidth: "150px",
      height: "60px"
    },
    input: {
      display: "flex",
      padding: 0,
      height: "auto"
    },
    valueContainer: {
      display: "flex",
      flex: 1,
      whiteSpace: "nowrap",
      alignItems: "center",
      overflow: "hidden"
    },
    noOptionsMessage: {
      padding: theme.spacing(1)
    },
    placeholder: {
      position: "absolute",
      left: 2,
      bottom: 6,
      fontSize: theme.typography.body1.fontSize,
      maxHeight: "24px",
      overflowY: "hidden"
    },
    paper: {
      position: "absolute",
      minWidth: "100%",
      marginTop: theme.spacing(1)
    },
    singleValue: {
      fontSize: theme.typography.body1.fontSize,
      width: "calc(100% - 8px)",
      overflow: "hidden",
      textOverflow: "ellipsis",
      textAlign: "left"
    }
  });

const inputComponent = ({ inputRef, ...props }: any) => {
  return <div ref={inputRef} {...props} />;
};

const Control = (props: any) => {
  const {
    children,
    innerProps,
    innerRef,
    isDisabled = false,
    selectProps: { classes, TextFieldProps }
  } = props;
  return (
    <TextField
      fullWidth
      disabled={isDisabled}
      InputProps={{
        inputComponent,
        inputProps: {
          className: classes.input,
          ref: innerRef,
          children,
          ...innerProps
        }
      }}
      {...TextFieldProps}
    />
  );
};

const NoOptionsMessage = (props: any) => {
  return (
    <Typography
      color="textSecondary"
      className={props.selectProps.classes.noOptionsMessage}
      {...props.innerProps}
    >
      {props.children}
    </Typography>
  );
};

const Option = (props: any) => {
  return (
    <MenuItem
      selected={props.isFocused}
      component="div"
      style={{
        fontWeight: props.isSelected ? 500 : 400
      }}
      {...props.innerProps}
    >
      {props.children}
    </MenuItem>
  );
};

const Menu = (props: any) => (
  <Paper
    square
    className={props.selectProps.classes.paper}
    {...props.innerProps}
  >
    {props.children}
  </Paper>
);

const Placeholder = (props: any) => {
  return (
    <Typography
      color="textSecondary"
      className={props.selectProps.classes.placeholder}
      {...props.innerProps}
    >
      {props.children}
    </Typography>
  );
};

const SingleValue = (props: any) => {
  return (
    <Typography
      className={props.selectProps.classes.singleValue}
      {...props.innerProps}
    >
      {props.children}
    </Typography>
  );
};

const ValueContainer = (props: any) => {
  return (
    <div className={props.selectProps.classes.valueContainer}>
      {props.children}
    </div>
  );
};

class TypeaheadSelect extends React.Component<Props, State> {
  components: any;
  constructor(props: Props) {
    super(props);
    this.components = {
      Control,
      Menu,
      NoOptionsMessage,
      Option,
      Placeholder,
      SingleValue,
      ValueContainer
    };
  }

  render() {
    const {
      classes,
      className,
      id,
      label,
      placeholder,
      onChange,
      suggestions,
      theme,
      value,
      isDisabled = false
    } = this.props;

    const selectStyles = {
      input: (base: any) => ({
        ...base,
        color: theme.palette.text.primary,
        "& input": {
          font: "inherit"
        }
      })
    };

    return (
      <Select
        className={classNames(classes.root, className)}
        classes={classes}
        menuPortalTarget={document.body}
        styles={selectStyles}
        inputId={id}
        isClearable={true}
        isDisabled={isDisabled}
        TextFieldProps={{
          label,
          InputLabelProps: {
            htmlFor: id,
            shrink: true
          }
        }}
        options={suggestions}
        components={this.components}
        value={value ? { value, label: value } : null}
        onChange={onChange}
        placeholder={placeholder}
      />
    );
  }
}

const styled = withStyles(styles, { withTheme: true })(TypeaheadSelect);
export { styled as TypeaheadSelect };
